import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { FaMinus, FaCheck } from "react-icons/fa";
import { PopoverContent } from "../ui/Popover";

const DropdownCheckbox = ({
  options = [],
  selectedValues,
  onSelectionChange,
  selectAllText = "Select All",
  clearAllText = "Clear All",
  applyText = "Apply",
  searchPlaceholder = "Search...",
  onClose,
  width = "w-[206px]",
  height = "max-h-[376px]",
  innerHeight = "max-h-[220px]",
  searchBar = true,
  search = "",
  setSearch,
  type,
  SetSelectedValuesId,
  label = false,
  isOpen = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelected, setLocalSelected] = useState({});
  const selectAllRef = useRef(null);

  useEffect(() => {
    if (!searchBar) {
      setSearchTerm(search);
    }
  }, [search, searchBar, searchTerm]);

  useEffect(() => {
    setSearchTerm("");
  }, [isOpen]);

  useEffect(() => {
    const initialSelected = {};
    const isAllSelected =
      selectedValues?.length === 1 && selectedValues?.[0] === "All";

    options?.forEach((option) => {
      initialSelected[option.value] =
        isAllSelected || selectedValues?.includes(option.value);
    });
    setLocalSelected(initialSelected);
  }, [options, selectedValues, isOpen]);

  useLayoutEffect(() => {
    if (!selectAllRef.current) return;
    const selectedCount = Object.values(localSelected).filter(Boolean).length;
    selectAllRef.current.indeterminate =
      selectedCount > 0 && selectedCount < options?.length;
  }, [localSelected, options?.length]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options?.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );
  }, [options, searchTerm]);

  const selectedOptions = useMemo(() => {
    return options?.filter((option) => localSelected[option.value]);
  }, [options, localSelected]);

  const toggleOption = (value) => {
    setLocalSelected((prev) => ({
      ...prev,
      [value]: !prev[value],
    }));
  };

  const handleSelectAllClick = () => {
    const selectedCount = Object.values(localSelected).filter(Boolean).length;
    const allSelected = selectedCount === options?.length;
    const someSelected = selectedCount > 0 && selectedCount < options?.length;

    if (allSelected || someSelected) {
      clearAll();
    } else {
      const newSelected = {};
      options?.forEach((option) => {
        newSelected[option.value] = true;
      });
      setLocalSelected(newSelected);
    }
  };

  const clearAll = () => {
    const cleared = {};
    options?.forEach((option) => {
      cleared[option.value] = false;
    });
    setLocalSelected(cleared);
  };

  const applySelections = () => {
    const selected = Object.entries(localSelected)
      .filter(([_, isSelected]) => isSelected)
      .map(([value]) => value);

    const allSelected = selected.length === options?.length;

    const selectedOptions = options?.filter((option) =>
      selected.includes(option.value)
    );

    const finalOutput =
      type === "Journal" && label
        ? selectedOptions.map((option) => option.label)
        : allSelected && type !== "Journal"
        ? ["All"]
        : selected;

    onSelectionChange(finalOutput);

    if (SetSelectedValuesId) {
      SetSelectedValuesId(selectedOptions);
    }

    setSearchTerm("");
    if (setSearch) setSearch("");
    if (onClose) onClose();
  };

  const selectedCount = Object.values(localSelected).filter(Boolean).length;
  const allSelected = selectedCount === options?.length;
  const someSelected = selectedCount > 0 && selectedCount < options?.length;

  // Determine if we should show selected options when search yields no results
  const showSelectedWhenNoResults =
    searchTerm && filteredOptions.length === 0 && selectedOptions.length > 0;

  return (
    <PopoverContent
      className={`${width} ${height} bg-white border border-gray-300 rounded-lg shadow-lg z-[70] p-0`}
      align="end"
      sideOffset={5}
    >
      {/* Search */}
      {searchBar && (
        <div>
          <div className="p-2">
            <input
              type="text"
              placeholder={searchPlaceholder}
              className="w-full p-2 border h-[28px] border-[#C3C3C3] rounded text-[13px] focus:border-[#35424D] focus:border focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <hr className="border-gray-300" />
        </div>
      )}

      {/* Select All */}
      <div className="p-2 hover:bg-gray-50">
        <label className="flex items-center space-x-2 cursor-pointer">
          <div
            className={`relative h-4 w-4 flex items-center justify-center border rounded 
              ${
                allSelected || someSelected
                  ? "bg-[#0060df] border-[#0060df]"
                  : "border-gray-400"
              }`}
          >
            <input
              ref={selectAllRef}
              type="checkbox"
              className="absolute opacity-0 h-full w-full cursor-pointer"
              checked={allSelected}
              onChange={handleSelectAllClick}
            />
            {allSelected ? (
              <span className="text-white text-[10px]">
                <FaCheck />
              </span>
            ) : someSelected ? (
              <span className="text-white text-[10px]">
                <FaMinus />
              </span>
            ) : null}
          </div>
          <span className="select-none text-[#35424D] text-[13px] font-medium">
            {selectAllText}
          </span>
        </label>
      </div>
      <div className={`${innerHeight} overflow-y-auto`}>
        {/* Individual Items */}
        {showSelectedWhenNoResults ? (
          // Show selected options when search yields no results but there are selected options
          selectedOptions.map((option) => (
            <div key={option.value} className="p-2 pl-6 hover:bg-gray-50">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 text-[#1C40C9] rounded"
                  checked={true}
                  onChange={() => toggleOption(option.value)}
                />
                <span className="select-none text-[#35424D] text-[13px] font-medium">
                  {option.label}
                </span>
              </label>
            </div>
          ))
        ) : filteredOptions.length > 0 ? (
          // Show filtered options when search yields results
          filteredOptions.map((option) => (
            <div key={option.value} className="p-2 pl-6 hover:bg-gray-50">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 text-[#1C40C9] rounded"
                  checked={localSelected[option.value] || false}
                  onChange={() => toggleOption(option.value)}
                />
                <span className="select-none text-[#35424D] text-[13px] font-medium">
                  {option.label}
                </span>
              </label>
            </div>
          ))
        ) : (
          // Show "No options found" only when there are no selected options
          <div className="p-2 text-center text-sm text-gray-500">
            No options found
          </div>
        )}
      </div>

      {/* Footer Controls */}
      <div className="flex items-center justify-between p-2 border-t border-gray-300">
        <span className="h-[16px] text-[11px] font-medium text-[#5D6871]">
          {selectedCount} of {options.length} Selected
        </span>
        <button
          className="text-[#1C40C9] text-[11px] font-medium h-[16px] focus:outline-none"
          onClick={clearAll}
        >
          {clearAllText}
        </button>
      </div>

      {/* Apply */}
      <div className=" px-2 flex items-center py-1 border-t border-gray-300">
        <button
          className="w-full text-white bg-[#04239A] hover:bg-[#1C40C9] h-[32px] rounded text-[13px] transition-colors"
          onClick={applySelections}
        >
          {applyText}
        </button>
      </div>
    </PopoverContent>
  );
};

export default DropdownCheckbox;
